import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2, Search, Users, Wallet, ArrowLeftRight, CheckCircle, Pause, Eye, RefreshCw,
  Plus, UserPlus, Plug, Settings, CreditCard, Building, Mail, Phone, Calendar, Hash,
  Shield, AlertCircle, ChevronRight, Copy, Ban, Activity, FileText, Database, Key, Globe,
  Edit, Trash2, RotateCcw, DollarSign, Percent, Save, ClipboardList,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { ContractStatusBadge, KycStatusBadge } from '../../components/ui/ContractStatusBadge';
import { VirtualAccountContractModal } from '../../components/admin/VirtualAccountContractModal';
import { api } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';

// ============================================
// INTERFACES
// ============================================

type TenantModality = 'INDUSTRIA' | 'COMERCIO' | 'AGRO' | 'SERVICOS' | 'FINTECH' | 'OUTROS';

interface Tenant {
  id: string;
  name: string;
  cnpj: string;
  tradeName?: string;
  email?: string;
  phone?: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'INACTIVE';
  modality?: TenantModality;
  tier?: 'BASIC' | 'STANDARD' | 'PREMIUM';
  usersCount: number;
  accountsCount: number;
  transactionsCount: number;
  virtualAccountsCount?: number;
  totalVolume: number;
  createdAt: string;
  users?: TenantUser[];
  accounts?: TenantAccount[];
  bankConnections?: BankConnection[];
}

interface TenantUser {
  id: string;
  name: string;
  email: string;
  cpfCnpj?: string;
  phone?: string;
  type: string;
  status: string;
  isSuperAdmin: boolean;
  createdAt: string;
}

interface TenantAccount {
  id: string;
  name: string;
  type: string;
  status: string;
  balance: number;
  pixKey?: string;
  pixKeyType?: string;
  entity?: { id: string; name: string; document: string };
  bankConnection?: { id: string; name: string; provider: string };
  _count?: { virtualAccounts: number; transactions: number };
}

interface BankConnection {
  id: string;
  name: string;
  provider: string;
  status: string;
  isDefault: boolean;
  createdAt?: string;
  _count?: { accounts: number };
}

interface Entity {
  id: string;
  type: string;
  name: string;
  document: string;
  tradeName?: string;
  email: string;
  phone?: string;
  status: string;
  kycStatus: string;
  createdAt?: string;
  _count?: { accounts: number };
}

interface ProviderInstance {
  id: string;
  name: string;
  status: string;
  priority: number;
  webhookUrl?: string;
  totalRequests: number;
  successCount: number;
  lastRequestAt?: string;
  lastErrorAt?: string;
  lastError?: string;
  createdAt: string;
  template: { id: string; name: string; category: string; type: string };
  account?: { id: string; name: string; type: string };
}

interface ProviderTemplate {
  id: string;
  name: string;
  type: string;
  category: string;
  version: string;
  status: string;
  credentialSchema?: any;
  isPublic: boolean;
  rating?: number;
}

interface FeeRule {
  id: string;
  operationType: string;
  calculationType: string;
  percentageValue: number;
  fixedValue: number;
  minFee?: number;
  maxFee?: number;
  isActive: boolean;
}

interface VirtualAccount {
  id: string;
  name: string;
  status: string;
  availableBalance: number;
  blockedBalance: number;
  totalBalance: number;
  pixKey?: string;
  pixKeyType?: string;
  createdAt: string;
  user?: { id: string; name: string; email: string };
  escrowAccount?: { id: string; name: string };
  // Contract control fields
  contractStatus?: string;
  kycOriginStatus?: string;
  kycDestinationStatus?: string;
}

// ============================================
// CONSTANTS
// ============================================

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os Status' },
  { value: 'ACTIVE', label: 'Ativo' },
  { value: 'SUSPENDED', label: 'Suspenso' },
  { value: 'INACTIVE', label: 'Inativo' },
];

const MODALITY_OPTIONS = [
  { value: '', label: 'Todas as Modalidades' },
  { value: 'INDUSTRIA', label: 'Indústria' },
  { value: 'COMERCIO', label: 'Comércio' },
  { value: 'AGRO', label: 'Agronegócio' },
  { value: 'SERVICOS', label: 'Serviços' },
  { value: 'FINTECH', label: 'Fintech' },
  { value: 'OUTROS', label: 'Outros' },
];

const TIER_OPTIONS = [
  { value: 'BASIC', label: 'Básico' },
  { value: 'STANDARD', label: 'Standard' },
  { value: 'PREMIUM', label: 'Premium' },
];

const TIER_LABELS: Record<string, string> = {
  BASIC: 'Básico',
  STANDARD: 'Standard',
  PREMIUM: 'Premium',
};

const TIER_COLORS: Record<string, string> = {
  BASIC: 'bg-gray-100 text-gray-700',
  STANDARD: 'bg-blue-100 text-blue-700',
  PREMIUM: 'bg-amber-100 text-amber-700',
};

const MODALITY_LABELS: Record<string, string> = {
  INDUSTRIA: 'Indústria',
  COMERCIO: 'Comércio',
  AGRO: 'Agronegócio',
  SERVICOS: 'Serviços',
  FINTECH: 'Fintech',
  OUTROS: 'Outros',
};

const USER_TYPE_OPTIONS = [
  { value: 'ADMIN', label: 'Administrador' },
  { value: 'OPERATOR', label: 'Operador' },
  { value: 'COMPLIANCE', label: 'Compliance' },
  { value: 'EMPLOYEE', label: 'Funcionário' },
  { value: 'CUSTOMER', label: 'Cliente' },
  { value: 'SUPPLIER', label: 'Fornecedor' },
];

const USER_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Ativo' },
  { value: 'INACTIVE', label: 'Inativo' },
  { value: 'BLOCKED', label: 'Bloqueado' },
];

const PIX_KEY_TYPE_OPTIONS = [
  { value: 'CPF', label: 'CPF' },
  { value: 'CNPJ', label: 'CNPJ' },
  { value: 'EMAIL', label: 'E-mail' },
  { value: 'PHONE', label: 'Telefone' },
  { value: 'RANDOM', label: 'Chave Aleatória' },
];

const ENTITY_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Ativo' },
  { value: 'INACTIVE', label: 'Inativo' },
  { value: 'BLOCKED', label: 'Bloqueado' },
];

const KYC_STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pendente' },
  { value: 'APPROVED', label: 'Aprovado' },
  { value: 'REJECTED', label: 'Rejeitado' },
];

const ACCOUNT_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Ativo' },
  { value: 'PENDING', label: 'Pendente' },
  { value: 'BLOCKED', label: 'Bloqueado' },
];

const INSTANCE_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Ativo' },
  { value: 'PENDING', label: 'Pendente' },
  { value: 'TESTING', label: 'Testando' },
  { value: 'SUSPENDED', label: 'Suspenso' },
  { value: 'ERROR', label: 'Erro' },
  { value: 'DEPRECATED', label: 'Descontinuado' },
];

const OPERATION_TYPE_OPTIONS = [
  { value: 'PIX_RECEIVE', label: 'Recebimento Pix' },
  { value: 'PIX_SEND', label: 'Envio Pix' },
  { value: 'TRANSFER', label: 'Transferência' },
  { value: 'BOLETO', label: 'Boleto' },
  { value: 'CARD', label: 'Cartão' },
  { value: 'WITHDRAWAL', label: 'Saque' },
  { value: 'MAINTENANCE', label: 'Manutenção' },
];

const CALCULATION_TYPE_OPTIONS = [
  { value: 'PERCENTAGE', label: 'Percentual' },
  { value: 'FIXED', label: 'Valor Fixo' },
  { value: 'PERCENTAGE_PLUS_FIXED', label: 'Percentual + Fixo' },
];

type TabKey = 'overview' | 'users' | 'entities' | 'accounts' | 'providers' | 'fees' | 'virtual' | 'setup';

// ============================================
// COMPONENT
// ============================================

export function SuperAdminTenants() {
  // State - Main
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modalityFilter, setModalityFilter] = useState('');
  
  // State - Selected tenant and tabs
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [detailsTab, setDetailsTab] = useState<TabKey>('overview');
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // State - Tab data (fetched from API)
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [tenantEntities, setTenantEntities] = useState<Entity[]>([]);
  const [tenantAccounts, setTenantAccounts] = useState<TenantAccount[]>([]);
  const [providerInstances, setProviderInstances] = useState<ProviderInstance[]>([]);
  const [providerTemplates, setProviderTemplates] = useState<ProviderTemplate[]>([]);
  const [feeRules, setFeeRules] = useState<FeeRule[]>([]);
  const [virtualAccounts, setVirtualAccounts] = useState<VirtualAccount[]>([]);
  
  // State - Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showCreateEntityModal, setShowCreateEntityModal] = useState(false);
  const [showEditEntityModal, setShowEditEntityModal] = useState(false);
  const [showEditAccountModal, setShowEditAccountModal] = useState(false);
  const [showCreateProviderModal, setShowCreateProviderModal] = useState(false);
  const [showEditProviderModal, setShowEditProviderModal] = useState(false);
  const [showCreateFeeRuleModal, setShowCreateFeeRuleModal] = useState(false);
  const [showEditFeeRuleModal, setShowEditFeeRuleModal] = useState(false);
  const [showEditTenantModal, setShowEditTenantModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{ type: string; id: string; name: string } | null>(null);
  const [showCreateVirtualAccountModal, setShowCreateVirtualAccountModal] = useState(false);
  const [showEditVirtualAccountModal, setShowEditVirtualAccountModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [selectedVirtualAccountForContract, setSelectedVirtualAccountForContract] = useState<VirtualAccount | null>(null);
  
  // State - Temp password display
  const [tempPassword, setTempPassword] = useState('');
  
  // State - Setup wizard
  const [setupStep, setSetupStep] = useState(1);
  const [createdEntity, setCreatedEntity] = useState<Entity | null>(null);
  const [createdBankConnection, setCreatedBankConnection] = useState<BankConnection | null>(null);
  
  // State - Edit items
  const [editingUser, setEditingUser] = useState<TenantUser | null>(null);
  const [editingEntity, setEditingEntity] = useState<Entity | null>(null);
  const [editingAccount, setEditingAccount] = useState<TenantAccount | null>(null);
  const [editingProvider, setEditingProvider] = useState<ProviderInstance | null>(null);
  const [editingFeeRule, setEditingFeeRule] = useState<FeeRule | null>(null);
  const [editingVirtualAccount, setEditingVirtualAccount] = useState<VirtualAccount | null>(null);

  // State - Form data
  const [newTenant, setNewTenant] = useState({ name: '', cnpj: '', tradeName: '', email: '', phone: '', modality: '' as TenantModality | '', tier: 'BASIC' as 'BASIC' | 'STANDARD' | 'PREMIUM' });
  const [editTenantData, setEditTenantData] = useState({ name: '', tradeName: '', email: '', phone: '', modality: '' as TenantModality | '', tier: 'BASIC' as 'BASIC' | 'STANDARD' | 'PREMIUM' });
  const [newUser, setNewUser] = useState({ name: '', email: '', cpfCnpj: '', phone: '', type: 'ADMIN' });
  const [editUserData, setEditUserData] = useState({ name: '', phone: '', type: '', status: '' });
  const [newEntity, setNewEntity] = useState({ type: 'COMPANY' as 'COMPANY' | 'INDIVIDUAL', name: '', document: '', tradeName: '', email: '', phone: '' });
  const [editEntityData, setEditEntityData] = useState({ name: '', tradeName: '', email: '', phone: '', status: '', kycStatus: '' });
  const [newConnection, setNewConnection] = useState({ name: '', provider: 'OPENPIX', apiKey: '', isDefault: true });
  const [editAccountData, setEditAccountData] = useState({ name: '', status: '', pixKey: '', pixKeyType: '' });
  const [newProvider, setNewProvider] = useState({ templateId: '', name: '', credentials: {} as Record<string, string> });
  const [editProviderData, setEditProviderData] = useState({ name: '', status: '', priority: 0 });
  const [newFeeRule, setNewFeeRule] = useState({ operationType: 'PIX_RECEIVE', calculationType: 'PERCENTAGE', percentageValue: 0, fixedValue: 0, minFee: 0, maxFee: 0 });
  const [editFeeRuleData, setEditFeeRuleData] = useState({ calculationType: '', percentageValue: 0, fixedValue: 0, minFee: 0, maxFee: 0, isActive: true });
  const [newAccount, setNewAccount] = useState({ name: '', pixKey: '', pixKeyType: 'RANDOM' });
  const [newVirtualAccount, setNewVirtualAccount] = useState({ userId: '', escrowAccountId: '', pixKey: '', pixKeyType: 'RANDOM' });
  const [editVirtualAccountData, setEditVirtualAccountData] = useState({ pixKey: '', pixKeyType: '', status: '' });

  // ============================================
  // DATA FETCHING
  // ============================================

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (modalityFilter) params.append('modality', modalityFilter);
      if (searchTerm) params.append('search', searchTerm);
      const response = await api.get(`/super-admin/tenants?${params.toString()}`);
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantDetails = async (tenantId: string) => {
    try {
      setLoadingDetails(true);
      const response = await api.get(`/super-admin/tenants/${tenantId}`);
      setSelectedTenant(response.data);
    } catch (error) {
      console.error('Error fetching tenant details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchTenantUsers = async (tenantId: string) => {
    try {
      const response = await api.get(`/super-admin/tenants/${tenantId}/users`);
      setTenantUsers(response.data);
    } catch (error) {
      console.error('Error fetching tenant users:', error);
    }
  };

  const fetchTenantEntities = async (tenantId: string) => {
    try {
      const response = await api.get(`/super-admin/tenants/${tenantId}/entities`);
      setTenantEntities(response.data);
    } catch (error) {
      console.error('Error fetching tenant entities:', error);
    }
  };

  const fetchTenantAccounts = async (tenantId: string) => {
    try {
      const response = await api.get(`/super-admin/tenants/${tenantId}/accounts`);
      setTenantAccounts(response.data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const fetchProviderInstances = async (tenantId: string) => {
    try {
      const response = await api.get(`/super-admin/tenants/${tenantId}/provider-instances`);
      setProviderInstances(response.data);
    } catch (error) {
      console.error('Error fetching provider instances:', error);
    }
  };

  const fetchProviderTemplates = async () => {
    try {
      const response = await api.get('/super-admin/provider-templates');
      setProviderTemplates(response.data);
    } catch (error) {
      console.error('Error fetching provider templates:', error);
    }
  };

  const fetchFeeRules = async (tenantId: string) => {
    try {
      const response = await api.get(`/super-admin/tenants/${tenantId}/fee-rules`);
      setFeeRules(response.data);
    } catch (error) {
      console.error('Error fetching fee rules:', error);
    }
  };

  const fetchVirtualAccounts = async (tenantId: string) => {
    try {
      const response = await api.get(`/super-admin/tenants/${tenantId}/virtual-accounts`);
      setVirtualAccounts(response.data);
    } catch (error) {
      console.error('Error fetching virtual accounts:', error);
    }
  };

  useEffect(() => {
    fetchTenants();
    fetchProviderTemplates();
  }, [statusFilter, modalityFilter, searchTerm]);

  // ============================================
  // HANDLERS - View Details
  // ============================================

  const handleViewDetails = async (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setDetailsTab('overview');
    await Promise.all([
      fetchTenantDetails(tenant.id),
      fetchTenantUsers(tenant.id),
      fetchTenantEntities(tenant.id),
      fetchTenantAccounts(tenant.id),
      fetchProviderInstances(tenant.id),
      fetchFeeRules(tenant.id),
      fetchVirtualAccounts(tenant.id),
    ]);
  };

  // ============================================
  // HANDLERS - Tenant CRUD
  // ============================================

  const handleUpdateStatus = async (tenantId: string, status: 'ACTIVE' | 'SUSPENDED' | 'INACTIVE') => {
    try {
      await api.put(`/super-admin/tenants/${tenantId}/status`, { status });
      fetchTenants();
      if (selectedTenant?.id === tenantId) fetchTenantDetails(tenantId);
    } catch (error) {
      console.error('Error updating tenant status:', error);
    }
  };

  const handleCreateTenant = async () => {
    try {
      const response = await api.post('/super-admin/tenants', newTenant);
      setShowCreateModal(false);
      setNewTenant({ name: '', cnpj: '', tradeName: '', email: '', phone: '', modality: '', tier: 'BASIC' });
      
      // Show temp password if admin was created
      if (response.data.tempPassword) {
        setTempPassword(response.data.tempPassword);
        setShowPasswordModal(true);
      }
      
      fetchTenants();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao criar tenant');
    }
  };

  const handleEditTenant = async () => {
    if (!selectedTenant) return;
    try {
      await api.put(`/super-admin/tenants/${selectedTenant.id}`, editTenantData);
      setShowEditTenantModal(false);
      fetchTenantDetails(selectedTenant.id);
      fetchTenants();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao atualizar tenant');
    }
  };

  const openEditTenantModal = () => {
    if (!selectedTenant) return;
    setEditTenantData({
      name: selectedTenant.name,
      tradeName: selectedTenant.tradeName || '',
      email: selectedTenant.email || '',
      phone: selectedTenant.phone || '',
      modality: selectedTenant.modality || '',
      tier: selectedTenant.tier || 'BASIC',
    });
    setShowEditTenantModal(true);
  };

  // ============================================
  // HANDLERS - User CRUD
  // ============================================

  const handleCreateUser = async () => {
    if (!selectedTenant) return;
    try {
      const response = await api.post(`/super-admin/tenants/${selectedTenant.id}/users`, newUser);
      if (response.data.tempPassword) {
        setTempPassword(response.data.tempPassword);
        setShowPasswordModal(true);
      }
      setShowCreateUserModal(false);
      setNewUser({ name: '', email: '', cpfCnpj: '', phone: '', type: 'ADMIN' });
      fetchTenantUsers(selectedTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao criar usuário');
    }
  };

  const handleEditUser = async () => {
    if (!selectedTenant || !editingUser) return;
    try {
      await api.put(`/super-admin/tenants/${selectedTenant.id}/users/${editingUser.id}`, editUserData);
      setShowEditUserModal(false);
      setEditingUser(null);
      fetchTenantUsers(selectedTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao atualizar usuário');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!selectedTenant) return;
    try {
      await api.delete(`/super-admin/tenants/${selectedTenant.id}/users/${userId}`);
      setShowDeleteConfirm(null);
      fetchTenantUsers(selectedTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao excluir usuário');
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (!selectedTenant) return;
    try {
      const response = await api.post(`/super-admin/tenants/${selectedTenant.id}/users/${userId}/reset-password`);
      setTempPassword(response.data.tempPassword);
      setShowPasswordModal(true);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao resetar senha');
    }
  };

  const openEditUserModal = (user: TenantUser) => {
    setEditingUser(user);
    setEditUserData({ name: user.name, phone: user.phone || '', type: user.type, status: user.status });
    setShowEditUserModal(true);
  };

  // ============================================
  // HANDLERS - Entity CRUD
  // ============================================

  const handleCreateEntity = async () => {
    if (!selectedTenant) return;
    try {
      await api.post(`/super-admin/tenants/${selectedTenant.id}/entities`, newEntity);
      setShowCreateEntityModal(false);
      setNewEntity({ type: 'COMPANY', name: '', document: '', tradeName: '', email: '', phone: '' });
      fetchTenantEntities(selectedTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao criar entidade');
    }
  };

  const handleEditEntity = async () => {
    if (!selectedTenant || !editingEntity) return;
    try {
      await api.put(`/super-admin/tenants/${selectedTenant.id}/entities/${editingEntity.id}`, editEntityData);
      setShowEditEntityModal(false);
      setEditingEntity(null);
      fetchTenantEntities(selectedTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao atualizar entidade');
    }
  };

  const handleDeleteEntity = async (entityId: string) => {
    if (!selectedTenant) return;
    try {
      await api.delete(`/super-admin/tenants/${selectedTenant.id}/entities/${entityId}`);
      setShowDeleteConfirm(null);
      fetchTenantEntities(selectedTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao excluir entidade');
    }
  };

  const openEditEntityModal = (entity: Entity) => {
    setEditingEntity(entity);
    setEditEntityData({
      name: entity.name,
      tradeName: entity.tradeName || '',
      email: entity.email,
      phone: entity.phone || '',
      status: entity.status,
      kycStatus: entity.kycStatus,
    });
    setShowEditEntityModal(true);
  };

  // ============================================
  // HANDLERS - Account CRUD
  // ============================================

  const handleEditAccount = async () => {
    if (!selectedTenant || !editingAccount) return;
    try {
      await api.put(`/super-admin/tenants/${selectedTenant.id}/accounts/${editingAccount.id}`, editAccountData);
      setShowEditAccountModal(false);
      setEditingAccount(null);
      fetchTenantAccounts(selectedTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao atualizar conta');
    }
  };

  const handleDeleteAccount = async (accountId: string) => {
    if (!selectedTenant) return;
    try {
      await api.delete(`/super-admin/tenants/${selectedTenant.id}/accounts/${accountId}`);
      setShowDeleteConfirm(null);
      fetchTenantAccounts(selectedTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao excluir conta');
    }
  };

  const openEditAccountModal = (account: TenantAccount) => {
    setEditingAccount(account);
    setEditAccountData({
      name: account.name,
      status: account.status,
      pixKey: account.pixKey || '',
      pixKeyType: account.pixKeyType || 'RANDOM',
    });
    setShowEditAccountModal(true);
  };

  // ============================================
  // HANDLERS - Provider Instance CRUD
  // ============================================

  const handleCreateProvider = async () => {
    if (!selectedTenant) return;
    try {
      await api.post(`/super-admin/tenants/${selectedTenant.id}/provider-instances`, newProvider);
      setShowCreateProviderModal(false);
      setNewProvider({ templateId: '', name: '', credentials: {} });
      fetchProviderInstances(selectedTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao criar provider instance');
    }
  };

  const handleEditProvider = async () => {
    if (!selectedTenant || !editingProvider) return;
    try {
      await api.put(`/super-admin/tenants/${selectedTenant.id}/provider-instances/${editingProvider.id}`, editProviderData);
      setShowEditProviderModal(false);
      setEditingProvider(null);
      fetchProviderInstances(selectedTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao atualizar provider');
    }
  };

  const handleDeleteProvider = async (instanceId: string) => {
    if (!selectedTenant) return;
    try {
      await api.delete(`/super-admin/tenants/${selectedTenant.id}/provider-instances/${instanceId}`);
      setShowDeleteConfirm(null);
      fetchProviderInstances(selectedTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao excluir provider');
    }
  };

  const openEditProviderModal = (instance: ProviderInstance) => {
    setEditingProvider(instance);
    setEditProviderData({ name: instance.name, status: instance.status, priority: instance.priority });
    setShowEditProviderModal(true);
  };

  // ============================================
  // HANDLERS - Fee Rule CRUD
  // ============================================

  const handleCreateFeeRule = async () => {
    if (!selectedTenant) return;
    try {
      await api.post(`/super-admin/tenants/${selectedTenant.id}/fee-rules`, newFeeRule);
      setShowCreateFeeRuleModal(false);
      setNewFeeRule({ operationType: 'PIX_RECEIVE', calculationType: 'PERCENTAGE', percentageValue: 0, fixedValue: 0, minFee: 0, maxFee: 0 });
      fetchFeeRules(selectedTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao criar regra de taxa');
    }
  };

  const handleEditFeeRule = async () => {
    if (!selectedTenant || !editingFeeRule) return;
    try {
      await api.put(`/super-admin/tenants/${selectedTenant.id}/fee-rules/${editingFeeRule.id}`, editFeeRuleData);
      setShowEditFeeRuleModal(false);
      setEditingFeeRule(null);
      fetchFeeRules(selectedTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao atualizar regra');
    }
  };

  const openEditFeeRuleModal = (rule: FeeRule) => {
    setEditingFeeRule(rule);
    setEditFeeRuleData({
      calculationType: rule.calculationType,
      percentageValue: rule.percentageValue,
      fixedValue: rule.fixedValue,
      minFee: rule.minFee || 0,
      maxFee: rule.maxFee || 0,
      isActive: rule.isActive,
    });
    setShowEditFeeRuleModal(true);
  };

  // ============================================
  // HANDLERS - Virtual Account CRUD
  // ============================================

  const handleCreateVirtualAccount = async () => {
    if (!selectedTenant) return;
    try {
      await api.post(`/super-admin/tenants/${selectedTenant.id}/virtual-accounts`, {
        userId: newVirtualAccount.userId,
        escrowAccountId: newVirtualAccount.escrowAccountId,
        pixKey: newVirtualAccount.pixKey || undefined,
        pixKeyType: newVirtualAccount.pixKeyType || undefined,
      });
      setShowCreateVirtualAccountModal(false);
      setNewVirtualAccount({ userId: '', escrowAccountId: '', pixKey: '', pixKeyType: 'RANDOM' });
      fetchVirtualAccounts(selectedTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao criar conta virtual');
    }
  };

  const handleEditVirtualAccount = async () => {
    if (!selectedTenant || !editingVirtualAccount) return;
    try {
      await api.put(`/super-admin/tenants/${selectedTenant.id}/virtual-accounts/${editingVirtualAccount.id}`, {
        pixKey: editVirtualAccountData.pixKey || undefined,
        pixKeyType: editVirtualAccountData.pixKeyType || undefined,
        status: editVirtualAccountData.status || undefined,
      });
      setShowEditVirtualAccountModal(false);
      setEditingVirtualAccount(null);
      fetchVirtualAccounts(selectedTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao atualizar conta virtual');
    }
  };

  const handleDeleteVirtualAccount = async (virtualAccountId: string) => {
    if (!selectedTenant) return;
    try {
      await api.delete(`/super-admin/tenants/${selectedTenant.id}/virtual-accounts/${virtualAccountId}`);
      setShowDeleteConfirm(null);
      fetchVirtualAccounts(selectedTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao excluir conta virtual');
    }
  };

  const openEditVirtualAccountModal = (va: VirtualAccount) => {
    setEditingVirtualAccount(va);
    setEditVirtualAccountData({
      pixKey: va.pixKey || '',
      pixKeyType: va.pixKeyType || 'RANDOM',
      status: va.status,
    });
    setShowEditVirtualAccountModal(true);
  };

  // ============================================
  // HANDLERS - Setup Wizard
  // ============================================

  const handleSetupEntity = async () => {
    if (!selectedTenant) return;
    try {
      const response = await api.post(`/super-admin/tenants/${selectedTenant.id}/entities`, newEntity);
      setCreatedEntity(response.data);
      setSetupStep(2);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao criar entidade');
    }
  };

  const handleSetupBankConnection = async () => {
    if (!selectedTenant) return;
    try {
      const response = await api.post(`/super-admin/tenants/${selectedTenant.id}/bank-connections`, {
        name: newConnection.name,
        provider: newConnection.provider,
        credentials: { apiKey: newConnection.apiKey },
        isDefault: newConnection.isDefault,
      });
      setCreatedBankConnection(response.data);
      setSetupStep(3);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao criar conexão bancária');
    }
  };

  const handleSetupAccount = async () => {
    if (!selectedTenant || !createdEntity || !createdBankConnection) return;
    try {
      await api.post(`/super-admin/tenants/${selectedTenant.id}/accounts/bolsao`, {
        name: newAccount.name,
        entityId: createdEntity.id,
        bankConnectionId: createdBankConnection.id,
        pixKey: newAccount.pixKey || undefined,
        pixKeyType: newAccount.pixKeyType,
      });
      setShowSetupWizard(false);
      resetSetupWizard();
      await handleViewDetails(selectedTenant);
      alert('Setup completo! Tenant configurado com sucesso.');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao criar conta bolsão');
    }
  };

  const resetSetupWizard = () => {
    setSetupStep(1);
    setCreatedEntity(null);
    setCreatedBankConnection(null);
    setNewEntity({ type: 'COMPANY', name: '', document: '', tradeName: '', email: '', phone: '' });
    setNewConnection({ name: '', provider: 'OPENPIX', apiKey: '', isDefault: true });
    setNewAccount({ name: '', pixKey: '', pixKeyType: 'RANDOM' });
  };

  // Initialize Setup Wizard with existing tenant data
  const initSetupWizardWithTenantData = async () => {
    if (!selectedTenant) return;
    
    // Pre-populate entity form with tenant data
    setNewEntity({
      type: 'COMPANY',
      name: selectedTenant.name,
      document: selectedTenant.cnpj,
      tradeName: selectedTenant.tradeName || '',
      email: selectedTenant.email || '',
      phone: selectedTenant.phone || '',
    });
    
    // Check if tenant already has entities
    await fetchTenantEntities(selectedTenant.id);
    
    // Check if tenant already has bank connections
    const connectionsResponse = await api.get(`/super-admin/tenants/${selectedTenant.id}/bank-connections`);
    const connections = connectionsResponse.data || [];
    
    // If tenant has root entity, skip to step 2
    const rootEntity = tenantEntities.find(e => e.document === selectedTenant.cnpj);
    if (rootEntity) {
      setCreatedEntity(rootEntity);
      
      // If tenant has bank connection, skip to step 3
      if (connections.length > 0) {
        setCreatedBankConnection(connections[0]);
        setSetupStep(3);
        setNewAccount({ name: `Conta Principal ${selectedTenant.tradeName || selectedTenant.name}`, pixKey: '', pixKeyType: 'RANDOM' });
      } else {
        setSetupStep(2);
        setNewConnection({ name: `${selectedTenant.tradeName || selectedTenant.name} - OpenPix`, provider: 'OPENPIX', apiKey: '', isDefault: true });
      }
    } else {
      setSetupStep(1);
    }
    
    setShowSetupWizard(true);
  };

  // ============================================
  // HELPERS
  // ============================================

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE': return <Badge variant="success"><CheckCircle className="w-3 h-3 mr-1" />Ativo</Badge>;
      case 'SUSPENDED': return <Badge variant="warning"><Pause className="w-3 h-3 mr-1" />Suspenso</Badge>;
      case 'INACTIVE': return <Badge variant="error"><Ban className="w-3 h-3 mr-1" />Inativo</Badge>;
      case 'BLOCKED': return <Badge variant="error"><Ban className="w-3 h-3 mr-1" />Bloqueado</Badge>;
      case 'ERROR': return <Badge variant="error"><AlertCircle className="w-3 h-3 mr-1" />Erro</Badge>;
      case 'PENDING': return <Badge variant="warning"><RefreshCw className="w-3 h-3 mr-1" />Pendente</Badge>;
      case 'TESTING': return <Badge variant="info"><Activity className="w-3 h-3 mr-1" />Testando</Badge>;
      case 'DEPRECATED': return <Badge variant="error"><Ban className="w-3 h-3 mr-1" />Descontinuado</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  const getUserTypeBadge = (type: string) => {
    const colors: Record<string, 'primary' | 'success' | 'warning' | 'info' | 'error'> = {
      ADMIN: 'primary', OPERATOR: 'info', COMPLIANCE: 'warning', EMPLOYEE: 'success', CUSTOMER: 'info', SUPPLIER: 'success',
    };
    return <Badge variant={colors[type] || 'primary'}>{type}</Badge>;
  };

  const getKycBadge = (status: string) => {
    switch (status) {
      case 'APPROVED': return <Badge variant="success">Aprovado</Badge>;
      case 'PENDING': return <Badge variant="warning">Pendente</Badge>;
      case 'REJECTED': return <Badge variant="error">Rejeitado</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'BAAS': return <CreditCard className="w-4 h-4" />;
      case 'CRYPTO': return <Database className="w-4 h-4" />;
      case 'CARDS': return <CreditCard className="w-4 h-4" />;
      default: return <Globe className="w-4 h-4" />;
    }
  };

  // ============================================
  // RENDER - Tab Content
  // ============================================

  const renderTabContent = () => {
    if (!selectedTenant) return null;

    switch (detailsTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card variant="glass" className="p-4 text-center">
                <Users className="w-8 h-8 mx-auto mb-2 text-primary-400" />
                <div className="text-2xl font-bold">{selectedTenant.usersCount}</div>
                <div className="text-sm text-surface-400">Usuários</div>
              </Card>
              <Card variant="glass" className="p-4 text-center">
                <Wallet className="w-8 h-8 mx-auto mb-2 text-accent-400" />
                <div className="text-2xl font-bold">{selectedTenant.accountsCount}</div>
                <div className="text-sm text-surface-400">Contas</div>
              </Card>
              <Card variant="glass" className="p-4 text-center">
                <ArrowLeftRight className="w-8 h-8 mx-auto mb-2 text-green-400" />
                <div className="text-2xl font-bold">{selectedTenant.transactionsCount}</div>
                <div className="text-sm text-surface-400">Transações</div>
              </Card>
              <Card variant="glass" className="p-4 text-center">
                <Activity className="w-8 h-8 mx-auto mb-2 text-yellow-400" />
                <div className="text-2xl font-bold">{formatCurrency(selectedTenant.totalVolume || 0)}</div>
                <div className="text-sm text-surface-400">Volume Total</div>
              </Card>
            </div>
            <Card variant="glass" className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2"><Building className="w-5 h-5" />Informações do Tenant</h3>
                <Button variant="ghost" size="sm" onClick={openEditTenantModal}><Edit className="w-4 h-4 mr-1" />Editar</Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-surface-800/50 rounded-lg">
                  <Building2 className="w-5 h-5 text-primary-400" />
                  <div><div className="text-xs text-surface-400">Razão Social</div><div className="font-medium">{selectedTenant.name}</div></div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-surface-800/50 rounded-lg">
                  <FileText className="w-5 h-5 text-primary-400" />
                  <div><div className="text-xs text-surface-400">Nome Fantasia</div><div className="font-medium">{selectedTenant.tradeName || '-'}</div></div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-surface-800/50 rounded-lg">
                  <Hash className="w-5 h-5 text-primary-400" />
                  <div><div className="text-xs text-surface-400">CNPJ</div><div className="font-medium font-mono">{selectedTenant.cnpj}</div></div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-surface-800/50 rounded-lg">
                  <Mail className="w-5 h-5 text-primary-400" />
                  <div><div className="text-xs text-surface-400">E-mail</div><div className="font-medium">{selectedTenant.email || '-'}</div></div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-surface-800/50 rounded-lg">
                  <Phone className="w-5 h-5 text-primary-400" />
                  <div><div className="text-xs text-surface-400">Telefone</div><div className="font-medium">{selectedTenant.phone || '-'}</div></div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-surface-800/50 rounded-lg">
                  <Building className="w-5 h-5 text-primary-400" />
                  <div><div className="text-xs text-surface-400">Modalidade</div><div className="font-medium">{selectedTenant.modality ? MODALITY_LABELS[selectedTenant.modality] : '-'}</div></div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-surface-800/50 rounded-lg">
                  <Calendar className="w-5 h-5 text-primary-400" />
                  <div><div className="text-xs text-surface-400">Criado em</div><div className="font-medium">{formatDate(selectedTenant.createdAt)}</div></div>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'users':
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Usuários ({tenantUsers.length})</h3>
              <Button onClick={() => setShowCreateUserModal(true)}><UserPlus className="w-4 h-4 mr-2" />Novo Usuário</Button>
            </div>
            {tenantUsers.length === 0 ? (
              <Card variant="glass" className="p-8 text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-surface-500" />
                <p className="text-surface-400">Nenhum usuário cadastrado</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {tenantUsers.map((user) => (
                  <Card key={user.id} variant="glass" className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                          <Users className="w-5 h-5 text-primary-400" />
                        </div>
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {user.name}
                            {user.isSuperAdmin && <Badge variant="error" className="text-xs"><Shield className="w-3 h-3 mr-1" />Super Admin</Badge>}
                          </div>
                          <div className="text-sm text-surface-400">{user.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getUserTypeBadge(user.type)}
                        {getStatusBadge(user.status)}
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditUserModal(user)}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleResetPassword(user.id)}><RotateCcw className="w-4 h-4" /></Button>
                          {!user.isSuperAdmin && (
                            <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm({ type: 'user', id: user.id, name: user.name })}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      case 'entities':
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">Titulares de Conta ({tenantEntities.length})</h3>
                <div className="relative group">
                  <AlertCircle className="w-4 h-4 text-surface-400 cursor-help" />
                  <div className="absolute left-0 bottom-full mb-2 w-72 p-3 bg-surface-800 border border-surface-600 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    <p className="text-sm text-surface-300">
                      <strong className="text-white">Entidades</strong> são pessoas jurídicas (CNPJ) ou físicas (CPF) que são titulares das contas bancárias do tenant.
                    </p>
                    <p className="text-xs text-surface-400 mt-2">
                      ⚠️ Não confundir com clientes (usuários com role CUSTOMER).
                    </p>
                  </div>
                </div>
              </div>
              <Button onClick={() => setShowCreateEntityModal(true)}><Plus className="w-4 h-4 mr-2" />Nova Entidade</Button>
            </div>
            {tenantEntities.length === 0 ? (
              <Card variant="glass" className="p-8 text-center">
                <Building className="w-12 h-12 mx-auto mb-4 text-surface-500" />
                <p className="text-surface-400">Nenhuma entidade cadastrada</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {tenantEntities.map((entity) => (
                  <Card key={entity.id} variant="glass" className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-accent-500/20 flex items-center justify-center">
                          {entity.type === 'COMPANY' ? <Building2 className="w-5 h-5 text-accent-400" /> : <Users className="w-5 h-5 text-accent-400" />}
                        </div>
                        <div>
                          <div className="font-medium">{entity.name}</div>
                          <div className="text-sm text-surface-400">{entity.type === 'COMPANY' ? 'CNPJ' : 'CPF'}: {entity.document}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getKycBadge(entity.kycStatus)}
                        {getStatusBadge(entity.status)}
                        <span className="text-sm text-surface-400">{entity._count?.accounts || 0} contas</span>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditEntityModal(entity)}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm({ type: 'entity', id: entity.id, name: entity.name })}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      case 'accounts':
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Contas ({tenantAccounts.length})</h3>
              <Button onClick={initSetupWizardWithTenantData}><Plus className="w-4 h-4 mr-2" />Nova Conta (Setup)</Button>
            </div>
            {tenantAccounts.length === 0 ? (
              <Card variant="glass" className="p-8 text-center">
                <Wallet className="w-12 h-12 mx-auto mb-4 text-surface-500" />
                <p className="text-surface-400">Nenhuma conta cadastrada</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {tenantAccounts.map((account) => (
                  <Card key={account.id} variant="glass" className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-accent-500/20 flex items-center justify-center">
                          <Wallet className="w-5 h-5 text-accent-400" />
                        </div>
                        <div>
                          <div className="font-medium">{account.name}</div>
                          <div className="text-sm text-surface-400">{account.type} • {account.entity?.name || '-'}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-sm text-surface-400">Saldo</div>
                          <div className="font-medium">{formatCurrency(account.balance)}</div>
                        </div>
                        {getStatusBadge(account.status)}
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditAccountModal(account)}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm({ type: 'account', id: account.id, name: account.name })}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      case 'providers':
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Provider Instances ({providerInstances.length})</h3>
              <Button onClick={() => setShowCreateProviderModal(true)}><Plus className="w-4 h-4 mr-2" />Novo Provider</Button>
            </div>
            {providerInstances.length === 0 ? (
              <Card variant="glass" className="p-8 text-center">
                <Plug className="w-12 h-12 mx-auto mb-4 text-surface-500" />
                <p className="text-surface-400">Nenhum provider configurado</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {providerInstances.map((instance) => (
                  <Card key={instance.id} variant="glass" className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                          {getCategoryIcon(instance.template.category)}
                        </div>
                        <div>
                          <div className="font-medium">{instance.name}</div>
                          <div className="text-sm text-surface-400">{instance.template.name} • {instance.template.category}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right text-sm">
                          <div className="text-surface-400">Requisições</div>
                          <div className="font-medium">{instance.successCount}/{instance.totalRequests}</div>
                        </div>
                        {getStatusBadge(instance.status)}
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditProviderModal(instance)}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm({ type: 'provider', id: instance.id, name: instance.name })}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                        </div>
                      </div>
                    </div>
                    {instance.lastError && (
                      <div className="mt-3 p-2 bg-red-500/10 border border-red-500/20 rounded text-sm text-red-400">
                        <AlertCircle className="w-4 h-4 inline mr-2" />{instance.lastError}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      case 'fees':
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Regras de Taxa ({feeRules.length})</h3>
              <Button onClick={() => setShowCreateFeeRuleModal(true)}><Plus className="w-4 h-4 mr-2" />Nova Regra</Button>
            </div>
            {feeRules.length === 0 ? (
              <Card variant="glass" className="p-8 text-center">
                <DollarSign className="w-12 h-12 mx-auto mb-4 text-surface-500" />
                <p className="text-surface-400">Nenhuma regra de taxa</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {feeRules.map((rule) => (
                  <Card key={rule.id} variant="glass" className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                          {rule.calculationType === 'PERCENTAGE' ? <Percent className="w-5 h-5 text-yellow-400" /> : <DollarSign className="w-5 h-5 text-yellow-400" />}
                        </div>
                        <div>
                          <div className="font-medium">{rule.operationType.replace(/_/g, ' ')}</div>
                          <div className="text-sm text-surface-400">
                            {rule.calculationType === 'PERCENTAGE' && `${rule.percentageValue}%`}
                            {rule.calculationType === 'FIXED' && formatCurrency(rule.fixedValue)}
                            {rule.calculationType === 'PERCENTAGE_PLUS_FIXED' && `${rule.percentageValue}% + ${formatCurrency(rule.fixedValue)}`}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={rule.isActive ? 'success' : 'error'}>{rule.isActive ? 'Ativo' : 'Inativo'}</Badge>
                        <Button variant="ghost" size="sm" onClick={() => openEditFeeRuleModal(rule)}><Edit className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      case 'virtual':
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Contas Virtuais ({virtualAccounts.length})</h3>
              <Button onClick={() => setShowCreateVirtualAccountModal(true)}><Plus className="w-4 h-4 mr-2" />Nova Conta Virtual</Button>
            </div>
            {virtualAccounts.length === 0 ? (
              <Card variant="glass" className="p-8 text-center">
                <Wallet className="w-12 h-12 mx-auto mb-4 text-surface-500" />
                <p className="text-surface-400">Nenhuma conta virtual</p>
                <p className="text-sm text-surface-500 mt-2">Crie uma conta virtual para um usuário do tenant</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {virtualAccounts.map((va) => (
                  <Card key={va.id} variant="glass" className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                          <Wallet className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                          <div className="font-medium">{va.name}</div>
                          <div className="text-sm text-surface-400">{va.user?.name || '-'} • {va.escrowAccount?.name || '-'}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-sm text-surface-400">Disponível</div>
                          <div className="font-medium text-green-400">{formatCurrency(va.availableBalance)}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-surface-400">Bloqueado</div>
                          <div className="font-medium text-yellow-400">{formatCurrency(va.blockedBalance)}</div>
                        </div>
                        {/* Contract Status Badges */}
                        <div className="flex flex-col gap-1">
                          {va.contractStatus && (
                            <ContractStatusBadge status={va.contractStatus} size="sm" />
                          )}
                          <div className="flex gap-1">
                            {va.kycOriginStatus && (
                              <KycStatusBadge status={va.kycOriginStatus} type="origin" size="sm" />
                            )}
                            {va.kycDestinationStatus && (
                              <KycStatusBadge status={va.kycDestinationStatus} type="destination" size="sm" />
                            )}
                          </div>
                        </div>
                        {getStatusBadge(va.status)}
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => {
                              setSelectedVirtualAccountForContract(va);
                              setShowContractModal(true);
                            }}
                            title="Gerenciar Contrato"
                          >
                            <ClipboardList className="w-4 h-4 text-primary-400" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditVirtualAccountModal(va)}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm({ type: 'virtualAccount', id: va.id, name: va.name })}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      case 'setup':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Setup do Tenant</h3>
            <Card variant="glass" className="p-6">
              <p className="text-surface-400 mb-4">Configure o tenant com entidade, conexão bancária e conta bolsão.</p>
              <Button onClick={initSetupWizardWithTenantData}><Settings className="w-4 h-4 mr-2" />Iniciar Setup Wizard</Button>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Building2 className="w-8 h-8 text-primary-400" />
            Gestão de Tenants
          </h1>
          <p className="text-surface-400 mt-1">Gerencie empresas, usuários e configurações</p>
        </div>
        <div className="flex gap-3">
          <Button variant="ghost" onClick={fetchTenants}><RefreshCw className="w-4 h-4 mr-2" />Atualizar</Button>
          <Button onClick={() => setShowCreateModal(true)}><Plus className="w-4 h-4 mr-2" />Novo Tenant</Button>
        </div>
      </div>

      {/* Filters */}
      <Card variant="glass" className="p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <Input placeholder="Buscar por nome, CNPJ ou e-mail..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} leftIcon={<Search className="w-4 h-4" />} />
          </div>
          <div className="w-48">
            <Select options={STATUS_OPTIONS} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} />
          </div>
          <div className="w-48">
            <Select options={MODALITY_OPTIONS} value={modalityFilter} onChange={(e) => setModalityFilter(e.target.value)} />
          </div>
        </div>
      </Card>

      {/* Tenants List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} variant="glass" className="p-6 animate-pulse">
              <div className="h-6 bg-surface-700 rounded w-3/4 mb-4" />
              <div className="h-4 bg-surface-700 rounded w-1/2 mb-2" />
              <div className="h-4 bg-surface-700 rounded w-2/3" />
            </Card>
          ))}
        </div>
      ) : tenants.length === 0 ? (
        <Card variant="glass" className="p-12 text-center">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-surface-500" />
          <h3 className="text-xl font-semibold mb-2">Nenhum tenant encontrado</h3>
          <p className="text-surface-400 mb-4">{searchTerm || statusFilter || modalityFilter ? 'Tente ajustar os filtros' : 'Crie o primeiro tenant'}</p>
          {!searchTerm && !statusFilter && !modalityFilter && <Button onClick={() => setShowCreateModal(true)}><Plus className="w-4 h-4 mr-2" />Criar Primeiro Tenant</Button>}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tenants.map((tenant) => (
            <motion.div key={tenant.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
              <Card variant="glass" className="p-6 cursor-pointer hover:border-primary-500/50 transition-all" onClick={() => handleViewDetails(tenant)}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-primary-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{tenant.name}</h3>
                      <p className="text-sm text-surface-400 font-mono">{tenant.cnpj}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {getStatusBadge(tenant.status)}
                    {tenant.tier && (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${TIER_COLORS[tenant.tier]}`}>
                        {TIER_LABELS[tenant.tier]}
                      </span>
                    )}
                    {tenant.modality && (
                      <Badge variant="info" className="text-xs">
                        {MODALITY_LABELS[tenant.modality] || tenant.modality}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="text-center p-2 bg-surface-800/50 rounded-lg">
                    <div className="text-lg font-bold">{tenant.usersCount}</div>
                    <div className="text-xs text-surface-400">Usuários</div>
                  </div>
                  <div className="text-center p-2 bg-surface-800/50 rounded-lg">
                    <div className="text-lg font-bold">{tenant.accountsCount}</div>
                    <div className="text-xs text-surface-400">Contas</div>
                  </div>
                  <div className="text-center p-2 bg-surface-800/50 rounded-lg">
                    <div className="text-lg font-bold">{tenant.transactionsCount}</div>
                    <div className="text-xs text-surface-400">Transações</div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-surface-400">Criado em {formatDate(tenant.createdAt)}</span>
                  <ChevronRight className="w-4 h-4 text-surface-400" />
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Tenant Details Modal */}
      <AnimatePresence>
        {selectedTenant && (
          <Modal isOpen={!!selectedTenant} onClose={() => setSelectedTenant(null)} title={selectedTenant.name} size="6xl">
            <div className="space-y-6">
              {/* Status and Actions */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getStatusBadge(selectedTenant.status)}
                  {selectedTenant.tier && (
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${TIER_COLORS[selectedTenant.tier]}`}>
                      {TIER_LABELS[selectedTenant.tier]}
                    </span>
                  )}
                  <span className="text-sm text-surface-400">ID: {selectedTenant.id}</span>
                  <button onClick={() => copyToClipboard(selectedTenant.id)} className="p-1 hover:bg-surface-700 rounded"><Copy className="w-4 h-4 text-surface-400" /></button>
                </div>
                <div className="flex gap-2">
                  {selectedTenant.status === 'ACTIVE' ? (
                    <Button variant="ghost" size="sm" onClick={() => handleUpdateStatus(selectedTenant.id, 'SUSPENDED')}><Pause className="w-4 h-4 mr-1" />Suspender</Button>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => handleUpdateStatus(selectedTenant.id, 'ACTIVE')}><CheckCircle className="w-4 h-4 mr-1" />Ativar</Button>
                  )}
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 p-1 bg-surface-800 rounded-lg overflow-x-auto">
                {[
                  { key: 'overview', label: 'Visão Geral', icon: Eye },
                  { key: 'users', label: 'Usuários', icon: Users },
                  { key: 'entities', label: 'Titulares', icon: Building },
                  { key: 'accounts', label: 'Contas', icon: Wallet },
                  { key: 'providers', label: 'Providers', icon: Globe },
                  { key: 'fees', label: 'Taxas', icon: DollarSign },
                  { key: 'virtual', label: 'Virtuais', icon: Database },
                  { key: 'setup', label: 'Setup', icon: Settings },
                ].map((tab) => (
                  <button key={tab.key} onClick={() => setDetailsTab(tab.key as TabKey)} className={`flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${detailsTab === tab.key ? 'bg-primary-500 text-white' : 'text-surface-400 hover:text-white hover:bg-surface-700'}`}>
                    <tab.icon className="w-4 h-4" />
                    <span className="hidden md:inline">{tab.label}</span>
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              {loadingDetails ? (
                <div className="flex items-center justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-primary-400" /></div>
              ) : (
                renderTabContent()
              )}
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Create Tenant Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Novo Tenant">
        <div className="space-y-4">
          <Input label="Razão Social" placeholder="Nome da empresa" value={newTenant.name} onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })} leftIcon={<Building2 className="w-4 h-4" />} />
          <Input label="CNPJ" placeholder="00.000.000/0000-00" value={newTenant.cnpj} onChange={(e) => setNewTenant({ ...newTenant, cnpj: e.target.value })} leftIcon={<Hash className="w-4 h-4" />} />
          <Input label="Nome Fantasia" placeholder="Nome fantasia (opcional)" value={newTenant.tradeName} onChange={(e) => setNewTenant({ ...newTenant, tradeName: e.target.value })} leftIcon={<FileText className="w-4 h-4" />} />
          <Select label="Modalidade" options={MODALITY_OPTIONS.filter(o => o.value !== '')} value={newTenant.modality} onChange={(e) => setNewTenant({ ...newTenant, modality: e.target.value as TenantModality })} />
          <Select label="Plano/Licença" options={TIER_OPTIONS} value={newTenant.tier} onChange={(e) => setNewTenant({ ...newTenant, tier: e.target.value as 'BASIC' | 'STANDARD' | 'PREMIUM' })} />
          <Input label="E-mail" type="email" placeholder="contato@empresa.com" value={newTenant.email} onChange={(e) => setNewTenant({ ...newTenant, email: e.target.value })} leftIcon={<Mail className="w-4 h-4" />} />
          <Input label="Telefone" placeholder="(00) 00000-0000" value={newTenant.phone} onChange={(e) => setNewTenant({ ...newTenant, phone: e.target.value })} leftIcon={<Phone className="w-4 h-4" />} />
          <div className="flex gap-3 pt-4">
            <Button variant="ghost" className="flex-1" onClick={() => setShowCreateModal(false)}>Cancelar</Button>
            <Button className="flex-1" onClick={handleCreateTenant}><Plus className="w-4 h-4 mr-2" />Criar Tenant</Button>
          </div>
        </div>
      </Modal>

      {/* Edit Tenant Modal */}
      <Modal isOpen={showEditTenantModal} onClose={() => setShowEditTenantModal(false)} title="Editar Tenant">
        <div className="space-y-4">
          <Input label="Razão Social" value={editTenantData.name} onChange={(e) => setEditTenantData({ ...editTenantData, name: e.target.value })} leftIcon={<Building2 className="w-4 h-4" />} />
          <Input label="Nome Fantasia" value={editTenantData.tradeName} onChange={(e) => setEditTenantData({ ...editTenantData, tradeName: e.target.value })} leftIcon={<FileText className="w-4 h-4" />} />
          <Select label="Modalidade" options={MODALITY_OPTIONS.filter(o => o.value !== '')} value={editTenantData.modality} onChange={(e) => setEditTenantData({ ...editTenantData, modality: e.target.value as TenantModality })} />
          <Select label="Plano/Licença" options={TIER_OPTIONS} value={editTenantData.tier} onChange={(e) => setEditTenantData({ ...editTenantData, tier: e.target.value as 'BASIC' | 'STANDARD' | 'PREMIUM' })} />
          <Input label="E-mail" type="email" value={editTenantData.email} onChange={(e) => setEditTenantData({ ...editTenantData, email: e.target.value })} leftIcon={<Mail className="w-4 h-4" />} />
          <Input label="Telefone" value={editTenantData.phone} onChange={(e) => setEditTenantData({ ...editTenantData, phone: e.target.value })} leftIcon={<Phone className="w-4 h-4" />} />
          <div className="flex gap-3 pt-4">
            <Button variant="ghost" className="flex-1" onClick={() => setShowEditTenantModal(false)}>Cancelar</Button>
            <Button className="flex-1" onClick={handleEditTenant}><Save className="w-4 h-4 mr-2" />Salvar</Button>
          </div>
        </div>
      </Modal>

      {/* Create User Modal */}
      <Modal isOpen={showCreateUserModal} onClose={() => setShowCreateUserModal(false)} title="Novo Usuário">
        <div className="space-y-4">
          <Input label="Nome Completo" placeholder="Nome do usuário" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} leftIcon={<Users className="w-4 h-4" />} />
          <Input label="E-mail" type="email" placeholder="usuario@empresa.com" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} leftIcon={<Mail className="w-4 h-4" />} />
          <Input label="CPF/CNPJ" placeholder="000.000.000-00" value={newUser.cpfCnpj} onChange={(e) => setNewUser({ ...newUser, cpfCnpj: e.target.value })} leftIcon={<Hash className="w-4 h-4" />} />
          <Input label="Telefone" placeholder="(00) 00000-0000" value={newUser.phone} onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })} leftIcon={<Phone className="w-4 h-4" />} />
          <Select label="Tipo de Usuário" options={USER_TYPE_OPTIONS} value={newUser.type} onChange={(e) => setNewUser({ ...newUser, type: e.target.value })} />
          <div className="p-3 bg-surface-800 rounded-lg text-sm text-surface-400"><AlertCircle className="w-4 h-4 inline mr-2" />Uma senha temporária será gerada automaticamente</div>
          <div className="flex gap-3 pt-4">
            <Button variant="ghost" className="flex-1" onClick={() => setShowCreateUserModal(false)}>Cancelar</Button>
            <Button className="flex-1" onClick={handleCreateUser}><UserPlus className="w-4 h-4 mr-2" />Criar Usuário</Button>
          </div>
        </div>
      </Modal>

      {/* Edit User Modal */}
      <Modal isOpen={showEditUserModal} onClose={() => { setShowEditUserModal(false); setEditingUser(null); }} title="Editar Usuário">
        <div className="space-y-4">
          <Input label="Nome" value={editUserData.name} onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })} leftIcon={<Users className="w-4 h-4" />} />
          <Input label="Telefone" value={editUserData.phone} onChange={(e) => setEditUserData({ ...editUserData, phone: e.target.value })} leftIcon={<Phone className="w-4 h-4" />} />
          <Select label="Tipo" options={USER_TYPE_OPTIONS} value={editUserData.type} onChange={(e) => setEditUserData({ ...editUserData, type: e.target.value })} />
          <Select label="Status" options={USER_STATUS_OPTIONS} value={editUserData.status} onChange={(e) => setEditUserData({ ...editUserData, status: e.target.value })} />
          <div className="flex gap-3 pt-4">
            <Button variant="ghost" className="flex-1" onClick={() => { setShowEditUserModal(false); setEditingUser(null); }}>Cancelar</Button>
            <Button className="flex-1" onClick={handleEditUser}><Save className="w-4 h-4 mr-2" />Salvar</Button>
          </div>
        </div>
      </Modal>

      {/* Create Entity Modal */}
      <Modal isOpen={showCreateEntityModal} onClose={() => setShowCreateEntityModal(false)} title="Nova Entidade">
        <div className="space-y-4">
          <Select label="Tipo de Entidade" options={[{ value: 'COMPANY', label: 'Pessoa Jurídica (CNPJ)' }, { value: 'INDIVIDUAL', label: 'Pessoa Física (CPF)' }]} value={newEntity.type} onChange={(e) => setNewEntity({ ...newEntity, type: e.target.value as 'COMPANY' | 'INDIVIDUAL' })} />
          <Input label="Nome/Razão Social" placeholder="Nome completo ou razão social" value={newEntity.name} onChange={(e) => setNewEntity({ ...newEntity, name: e.target.value })} leftIcon={<Building className="w-4 h-4" />} />
          <Input label={newEntity.type === 'COMPANY' ? 'CNPJ' : 'CPF'} placeholder={newEntity.type === 'COMPANY' ? '00.000.000/0000-00' : '000.000.000-00'} value={newEntity.document} onChange={(e) => setNewEntity({ ...newEntity, document: e.target.value })} leftIcon={<Hash className="w-4 h-4" />} />
          {newEntity.type === 'COMPANY' && <Input label="Nome Fantasia" placeholder="Nome fantasia (opcional)" value={newEntity.tradeName} onChange={(e) => setNewEntity({ ...newEntity, tradeName: e.target.value })} leftIcon={<FileText className="w-4 h-4" />} />}
          <Input label="E-mail" type="email" placeholder="contato@empresa.com" value={newEntity.email} onChange={(e) => setNewEntity({ ...newEntity, email: e.target.value })} leftIcon={<Mail className="w-4 h-4" />} />
          <Input label="Telefone" placeholder="(00) 00000-0000" value={newEntity.phone} onChange={(e) => setNewEntity({ ...newEntity, phone: e.target.value })} leftIcon={<Phone className="w-4 h-4" />} />
          <div className="flex gap-3 pt-4">
            <Button variant="ghost" className="flex-1" onClick={() => setShowCreateEntityModal(false)}>Cancelar</Button>
            <Button className="flex-1" onClick={handleCreateEntity}><Plus className="w-4 h-4 mr-2" />Criar Entidade</Button>
          </div>
        </div>
      </Modal>

      {/* Edit Entity Modal */}
      <Modal isOpen={showEditEntityModal} onClose={() => { setShowEditEntityModal(false); setEditingEntity(null); }} title="Editar Entidade">
        <div className="space-y-4">
          <Input label="Nome" value={editEntityData.name} onChange={(e) => setEditEntityData({ ...editEntityData, name: e.target.value })} leftIcon={<Building className="w-4 h-4" />} />
          <Input label="Nome Fantasia" value={editEntityData.tradeName} onChange={(e) => setEditEntityData({ ...editEntityData, tradeName: e.target.value })} leftIcon={<FileText className="w-4 h-4" />} />
          <Input label="E-mail" value={editEntityData.email} onChange={(e) => setEditEntityData({ ...editEntityData, email: e.target.value })} leftIcon={<Mail className="w-4 h-4" />} />
          <Input label="Telefone" value={editEntityData.phone} onChange={(e) => setEditEntityData({ ...editEntityData, phone: e.target.value })} leftIcon={<Phone className="w-4 h-4" />} />
          <Select label="Status" options={ENTITY_STATUS_OPTIONS} value={editEntityData.status} onChange={(e) => setEditEntityData({ ...editEntityData, status: e.target.value })} />
          <Select label="Status KYC" options={KYC_STATUS_OPTIONS} value={editEntityData.kycStatus} onChange={(e) => setEditEntityData({ ...editEntityData, kycStatus: e.target.value })} />
          <div className="flex gap-3 pt-4">
            <Button variant="ghost" className="flex-1" onClick={() => { setShowEditEntityModal(false); setEditingEntity(null); }}>Cancelar</Button>
            <Button className="flex-1" onClick={handleEditEntity}><Save className="w-4 h-4 mr-2" />Salvar</Button>
          </div>
        </div>
      </Modal>

      {/* Edit Account Modal */}
      <Modal isOpen={showEditAccountModal} onClose={() => { setShowEditAccountModal(false); setEditingAccount(null); }} title="Editar Conta">
        <div className="space-y-4">
          <Input label="Nome" value={editAccountData.name} onChange={(e) => setEditAccountData({ ...editAccountData, name: e.target.value })} leftIcon={<Wallet className="w-4 h-4" />} />
          <Select label="Status" options={ACCOUNT_STATUS_OPTIONS} value={editAccountData.status} onChange={(e) => setEditAccountData({ ...editAccountData, status: e.target.value })} />
          <Select label="Tipo de Chave Pix" options={PIX_KEY_TYPE_OPTIONS} value={editAccountData.pixKeyType} onChange={(e) => setEditAccountData({ ...editAccountData, pixKeyType: e.target.value })} />
          {editAccountData.pixKeyType !== 'RANDOM' && <Input label="Chave Pix" value={editAccountData.pixKey} onChange={(e) => setEditAccountData({ ...editAccountData, pixKey: e.target.value })} leftIcon={<Key className="w-4 h-4" />} />}
          <div className="flex gap-3 pt-4">
            <Button variant="ghost" className="flex-1" onClick={() => { setShowEditAccountModal(false); setEditingAccount(null); }}>Cancelar</Button>
            <Button className="flex-1" onClick={handleEditAccount}><Save className="w-4 h-4 mr-2" />Salvar</Button>
          </div>
        </div>
      </Modal>

      {/* Create Provider Instance Modal */}
      <Modal isOpen={showCreateProviderModal} onClose={() => setShowCreateProviderModal(false)} title="Novo Provider Instance">
        <div className="space-y-4">
          <Select label="Template" options={[{ value: '', label: 'Selecione um template...' }, ...providerTemplates.map(t => ({ value: t.id, label: `${t.name} (${t.category})` }))]} value={newProvider.templateId} onChange={(e) => setNewProvider({ ...newProvider, templateId: e.target.value })} />
          <Input label="Nome da Instância" placeholder="Ex: OpenPix Produção" value={newProvider.name} onChange={(e) => setNewProvider({ ...newProvider, name: e.target.value })} leftIcon={<Globe className="w-4 h-4" />} />
          <Input label="API Key" type="password" placeholder="Credencial principal" value={newProvider.credentials.apiKey || ''} onChange={(e) => setNewProvider({ ...newProvider, credentials: { ...newProvider.credentials, apiKey: e.target.value } })} leftIcon={<Key className="w-4 h-4" />} />
          <div className="flex gap-3 pt-4">
            <Button variant="ghost" className="flex-1" onClick={() => setShowCreateProviderModal(false)}>Cancelar</Button>
            <Button className="flex-1" onClick={handleCreateProvider}><Plus className="w-4 h-4 mr-2" />Criar Provider</Button>
          </div>
        </div>
      </Modal>

      {/* Edit Provider Instance Modal */}
      <Modal isOpen={showEditProviderModal} onClose={() => { setShowEditProviderModal(false); setEditingProvider(null); }} title="Editar Provider Instance">
        <div className="space-y-4">
          <Input label="Nome" value={editProviderData.name} onChange={(e) => setEditProviderData({ ...editProviderData, name: e.target.value })} leftIcon={<Globe className="w-4 h-4" />} />
          <Select label="Status" options={INSTANCE_STATUS_OPTIONS} value={editProviderData.status} onChange={(e) => setEditProviderData({ ...editProviderData, status: e.target.value })} />
          <Input label="Prioridade" type="number" value={editProviderData.priority.toString()} onChange={(e) => setEditProviderData({ ...editProviderData, priority: parseInt(e.target.value) || 0 })} leftIcon={<Activity className="w-4 h-4" />} />
          <div className="flex gap-3 pt-4">
            <Button variant="ghost" className="flex-1" onClick={() => { setShowEditProviderModal(false); setEditingProvider(null); }}>Cancelar</Button>
            <Button className="flex-1" onClick={handleEditProvider}><Save className="w-4 h-4 mr-2" />Salvar</Button>
          </div>
        </div>
      </Modal>

      {/* Create Fee Rule Modal */}
      <Modal isOpen={showCreateFeeRuleModal} onClose={() => setShowCreateFeeRuleModal(false)} title="Nova Regra de Taxa">
        <div className="space-y-4">
          <Select label="Tipo de Operação" options={OPERATION_TYPE_OPTIONS} value={newFeeRule.operationType} onChange={(e) => setNewFeeRule({ ...newFeeRule, operationType: e.target.value })} />
          <Select label="Tipo de Cálculo" options={CALCULATION_TYPE_OPTIONS} value={newFeeRule.calculationType} onChange={(e) => setNewFeeRule({ ...newFeeRule, calculationType: e.target.value })} />
          {(newFeeRule.calculationType === 'PERCENTAGE' || newFeeRule.calculationType === 'PERCENTAGE_PLUS_FIXED') && (
            <Input label="Percentual (%)" type="number" step="0.01" value={newFeeRule.percentageValue.toString()} onChange={(e) => setNewFeeRule({ ...newFeeRule, percentageValue: parseFloat(e.target.value) || 0 })} leftIcon={<Percent className="w-4 h-4" />} />
          )}
          {(newFeeRule.calculationType === 'FIXED' || newFeeRule.calculationType === 'PERCENTAGE_PLUS_FIXED') && (
            <Input label="Valor Fixo (centavos)" type="number" value={newFeeRule.fixedValue.toString()} onChange={(e) => setNewFeeRule({ ...newFeeRule, fixedValue: parseInt(e.target.value) || 0 })} leftIcon={<DollarSign className="w-4 h-4" />} />
          )}
          <div className="grid grid-cols-2 gap-4">
            <Input label="Taxa Mínima (centavos)" type="number" value={newFeeRule.minFee.toString()} onChange={(e) => setNewFeeRule({ ...newFeeRule, minFee: parseInt(e.target.value) || 0 })} />
            <Input label="Taxa Máxima (centavos)" type="number" value={newFeeRule.maxFee.toString()} onChange={(e) => setNewFeeRule({ ...newFeeRule, maxFee: parseInt(e.target.value) || 0 })} />
          </div>
          <div className="flex gap-3 pt-4">
            <Button variant="ghost" className="flex-1" onClick={() => setShowCreateFeeRuleModal(false)}>Cancelar</Button>
            <Button className="flex-1" onClick={handleCreateFeeRule}><Plus className="w-4 h-4 mr-2" />Criar Regra</Button>
          </div>
        </div>
      </Modal>

      {/* Edit Fee Rule Modal */}
      <Modal isOpen={showEditFeeRuleModal} onClose={() => { setShowEditFeeRuleModal(false); setEditingFeeRule(null); }} title="Editar Regra de Taxa">
        <div className="space-y-4">
          <Select label="Tipo de Cálculo" options={CALCULATION_TYPE_OPTIONS} value={editFeeRuleData.calculationType} onChange={(e) => setEditFeeRuleData({ ...editFeeRuleData, calculationType: e.target.value })} />
          {(editFeeRuleData.calculationType === 'PERCENTAGE' || editFeeRuleData.calculationType === 'PERCENTAGE_PLUS_FIXED') && (
            <Input label="Percentual (%)" type="number" step="0.01" value={editFeeRuleData.percentageValue.toString()} onChange={(e) => setEditFeeRuleData({ ...editFeeRuleData, percentageValue: parseFloat(e.target.value) || 0 })} leftIcon={<Percent className="w-4 h-4" />} />
          )}
          {(editFeeRuleData.calculationType === 'FIXED' || editFeeRuleData.calculationType === 'PERCENTAGE_PLUS_FIXED') && (
            <Input label="Valor Fixo (centavos)" type="number" value={editFeeRuleData.fixedValue.toString()} onChange={(e) => setEditFeeRuleData({ ...editFeeRuleData, fixedValue: parseInt(e.target.value) || 0 })} leftIcon={<DollarSign className="w-4 h-4" />} />
          )}
          <div className="grid grid-cols-2 gap-4">
            <Input label="Taxa Mínima" type="number" value={editFeeRuleData.minFee.toString()} onChange={(e) => setEditFeeRuleData({ ...editFeeRuleData, minFee: parseInt(e.target.value) || 0 })} />
            <Input label="Taxa Máxima" type="number" value={editFeeRuleData.maxFee.toString()} onChange={(e) => setEditFeeRuleData({ ...editFeeRuleData, maxFee: parseInt(e.target.value) || 0 })} />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={editFeeRuleData.isActive} onChange={(e) => setEditFeeRuleData({ ...editFeeRuleData, isActive: e.target.checked })} className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500" />
            <span className="text-sm">Regra ativa</span>
          </label>
          <div className="flex gap-3 pt-4">
            <Button variant="ghost" className="flex-1" onClick={() => { setShowEditFeeRuleModal(false); setEditingFeeRule(null); }}>Cancelar</Button>
            <Button className="flex-1" onClick={handleEditFeeRule}><Save className="w-4 h-4 mr-2" />Salvar</Button>
          </div>
        </div>
      </Modal>

      {/* Create Virtual Account Modal */}
      <Modal isOpen={showCreateVirtualAccountModal} onClose={() => setShowCreateVirtualAccountModal(false)} title="Nova Conta Virtual">
        <div className="space-y-4">
          <Select 
            label="Usuário" 
            options={[{ value: '', label: 'Selecione um usuário...' }, ...tenantUsers.map(u => ({ value: u.id, label: `${u.name} (${u.email})` }))]} 
            value={newVirtualAccount.userId} 
            onChange={(e) => setNewVirtualAccount({ ...newVirtualAccount, userId: e.target.value })} 
          />
          <Select 
            label="Conta Bolsão (Escrow)" 
            options={[{ value: '', label: 'Selecione uma conta bolsão...' }, ...tenantAccounts.filter(a => a.type === 'BOLSAO').map(a => ({ value: a.id, label: a.name }))]} 
            value={newVirtualAccount.escrowAccountId} 
            onChange={(e) => setNewVirtualAccount({ ...newVirtualAccount, escrowAccountId: e.target.value })} 
          />
          <Select label="Tipo de Chave Pix" options={PIX_KEY_TYPE_OPTIONS} value={newVirtualAccount.pixKeyType} onChange={(e) => setNewVirtualAccount({ ...newVirtualAccount, pixKeyType: e.target.value })} />
          {newVirtualAccount.pixKeyType !== 'RANDOM' && (
            <Input label="Chave Pix" placeholder="Informe a chave Pix" value={newVirtualAccount.pixKey} onChange={(e) => setNewVirtualAccount({ ...newVirtualAccount, pixKey: e.target.value })} leftIcon={<Key className="w-4 h-4" />} />
          )}
          <div className="p-3 bg-surface-800 rounded-lg text-sm text-surface-400">
            <AlertCircle className="w-4 h-4 inline mr-2" />
            A conta virtual será vinculada ao usuário selecionado e herdará recursos do bolsão.
          </div>
          <div className="flex gap-3 pt-4">
            <Button variant="ghost" className="flex-1" onClick={() => setShowCreateVirtualAccountModal(false)}>Cancelar</Button>
            <Button className="flex-1" onClick={handleCreateVirtualAccount} disabled={!newVirtualAccount.userId || !newVirtualAccount.escrowAccountId}><Plus className="w-4 h-4 mr-2" />Criar Conta Virtual</Button>
          </div>
        </div>
      </Modal>

      {/* Edit Virtual Account Modal */}
      <Modal isOpen={showEditVirtualAccountModal} onClose={() => { setShowEditVirtualAccountModal(false); setEditingVirtualAccount(null); }} title="Editar Conta Virtual">
        <div className="space-y-4">
          <Select label="Tipo de Chave Pix" options={PIX_KEY_TYPE_OPTIONS} value={editVirtualAccountData.pixKeyType} onChange={(e) => setEditVirtualAccountData({ ...editVirtualAccountData, pixKeyType: e.target.value })} />
          {editVirtualAccountData.pixKeyType !== 'RANDOM' && (
            <Input label="Chave Pix" value={editVirtualAccountData.pixKey} onChange={(e) => setEditVirtualAccountData({ ...editVirtualAccountData, pixKey: e.target.value })} leftIcon={<Key className="w-4 h-4" />} />
          )}
          <Select 
            label="Status" 
            options={[
              { value: 'ACTIVE', label: 'Ativo' },
              { value: 'BLOCKED', label: 'Bloqueado' },
              { value: 'CLOSED', label: 'Fechado' },
            ]} 
            value={editVirtualAccountData.status} 
            onChange={(e) => setEditVirtualAccountData({ ...editVirtualAccountData, status: e.target.value })} 
          />
          <div className="flex gap-3 pt-4">
            <Button variant="ghost" className="flex-1" onClick={() => { setShowEditVirtualAccountModal(false); setEditingVirtualAccount(null); }}>Cancelar</Button>
            <Button className="flex-1" onClick={handleEditVirtualAccount}><Save className="w-4 h-4 mr-2" />Salvar</Button>
          </div>
        </div>
      </Modal>

      {/* Password Display Modal */}
      <Modal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} title="Senha Temporária">
        <div className="space-y-4 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
            <Key className="w-8 h-8 text-green-400" />
          </div>
          <p className="text-surface-400">Anote a senha temporária:</p>
          <div className="flex items-center justify-center gap-2 p-4 bg-surface-800 rounded-lg">
            <code className="text-xl font-mono text-primary-400">{tempPassword}</code>
            <button onClick={() => copyToClipboard(tempPassword)} className="p-2 hover:bg-surface-700 rounded"><Copy className="w-5 h-5 text-surface-400" /></button>
          </div>
          <p className="text-sm text-surface-500">O usuário deverá alterar a senha no primeiro acesso</p>
          <div className="flex gap-3 pt-2">
            <Button 
              variant="ghost" 
              className="flex-1"
              onClick={() => {
                const userEmail = editingUser?.email || newUser.email || '';
                const platformUrl = window.location.origin;
                const message = encodeURIComponent(
                  `Olá! Suas credenciais de acesso à plataforma UniãoBR:\n\n` +
                  `📧 Email: ${userEmail}\n` +
                  `🔑 Senha temporária: ${tempPassword}\n` +
                  `🌐 Acesse: ${platformUrl}\n\n` +
                  `⚠️ Por segurança, altere sua senha no primeiro acesso.`
                );
                window.open(`https://wa.me/?text=${message}`, '_blank');
              }}
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Compartilhar via WhatsApp
            </Button>
            <Button className="flex-1" onClick={() => setShowPasswordModal(false)}>Entendi</Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!showDeleteConfirm} onClose={() => setShowDeleteConfirm(null)} title="Confirmar Exclusão">
        <div className="space-y-4 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-red-500/20 flex items-center justify-center">
            <Trash2 className="w-8 h-8 text-red-400" />
          </div>
          <p className="text-surface-300">Tem certeza que deseja excluir <strong>{showDeleteConfirm?.name}</strong>?</p>
          <p className="text-sm text-surface-500">Esta ação não pode ser desfeita.</p>
          <div className="flex gap-3 pt-4">
            <Button variant="ghost" className="flex-1" onClick={() => setShowDeleteConfirm(null)}>Cancelar</Button>
            <Button variant="danger" className="flex-1" onClick={() => {
              if (!showDeleteConfirm) return;
              switch (showDeleteConfirm.type) {
                case 'user': handleDeleteUser(showDeleteConfirm.id); break;
                case 'entity': handleDeleteEntity(showDeleteConfirm.id); break;
                case 'account': handleDeleteAccount(showDeleteConfirm.id); break;
                case 'provider': handleDeleteProvider(showDeleteConfirm.id); break;
                case 'virtualAccount': handleDeleteVirtualAccount(showDeleteConfirm.id); break;
              }
            }}><Trash2 className="w-4 h-4 mr-2" />Excluir</Button>
          </div>
        </div>
      </Modal>

      {/* Setup Wizard Modal - Professional Design */}
      <Modal isOpen={showSetupWizard} onClose={() => { setShowSetupWizard(false); resetSetupWizard(); }} title="" size="xl">
        <div className="min-h-[600px]">
          {/* Header with Tenant Info */}
          <div className="bg-gradient-to-r from-primary-600/20 to-accent-600/20 -mx-6 -mt-6 px-8 py-6 mb-8 border-b border-surface-700">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-primary-500/20 flex items-center justify-center">
                <Settings className="w-7 h-7 text-primary-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Configuração Inicial do Tenant</h2>
                <p className="text-surface-400 mt-1">
                  {selectedTenant?.name} • Configure entidade, conexão bancária e conta principal
                </p>
              </div>
            </div>
          </div>

          {/* Progress Steps - Horizontal Timeline */}
          <div className="mb-10 px-4">
            <div className="flex items-center justify-between relative">
              {/* Progress Line Background */}
              <div className="absolute top-6 left-[60px] right-[60px] h-1 bg-surface-700 rounded-full" />
              {/* Progress Line Active */}
              <div 
                className="absolute top-6 left-[60px] h-1 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full transition-all duration-500"
                style={{ width: `calc(${((setupStep - 1) / 2) * 100}% - ${setupStep === 1 ? 0 : setupStep === 2 ? 60 : 120}px)` }}
              />
              
              {[
                { step: 1, label: 'Entidade', sublabel: 'Titular da Conta', icon: Building2 },
                { step: 2, label: 'Conexão Bancária', sublabel: 'Provider de Pagamento', icon: Plug },
                { step: 3, label: 'Conta Bolsão', sublabel: 'Conta Principal', icon: Wallet }
              ].map((item) => (
                <div key={item.step} className="flex flex-col items-center relative z-10">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold transition-all duration-300 ${
                    setupStep > item.step 
                      ? 'bg-green-500 text-white shadow-lg shadow-green-500/30' 
                      : setupStep === item.step 
                        ? 'bg-gradient-to-br from-primary-500 to-accent-500 text-white shadow-lg shadow-primary-500/30 scale-110' 
                        : 'bg-surface-700 text-surface-400'
                  }`}>
                    {setupStep > item.step ? <CheckCircle className="w-6 h-6" /> : <item.icon className="w-6 h-6" />}
                  </div>
                  <span className={`mt-3 text-sm font-semibold transition-colors ${
                    setupStep >= item.step ? 'text-white' : 'text-surface-500'
                  }`}>{item.label}</span>
                  <span className={`text-xs transition-colors ${
                    setupStep >= item.step ? 'text-surface-400' : 'text-surface-600'
                  }`}>{item.sublabel}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="px-4">
            {/* Step 1: Entity */}
            {setupStep === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">Criar Entidade</h3>
                    <p className="text-sm text-surface-400">A entidade representa a pessoa jurídica ou física titular das contas</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <Select 
                      label="Tipo de Entidade" 
                      options={[
                        { value: 'COMPANY', label: '🏢 Pessoa Jurídica (CNPJ)' }, 
                        { value: 'INDIVIDUAL', label: '👤 Pessoa Física (CPF)' }
                      ]} 
                      value={newEntity.type} 
                      onChange={(e) => setNewEntity({ ...newEntity, type: e.target.value as 'COMPANY' | 'INDIVIDUAL' })} 
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Input 
                      label={newEntity.type === 'COMPANY' ? 'Razão Social' : 'Nome Completo'}
                      placeholder={newEntity.type === 'COMPANY' ? 'Empresa Exemplo LTDA' : 'João da Silva'}
                      value={newEntity.name} 
                      onChange={(e) => setNewEntity({ ...newEntity, name: e.target.value })} 
                      leftIcon={<Building className="w-4 h-4" />} 
                    />
                  </div>
                  
                  <Input 
                    label={newEntity.type === 'COMPANY' ? 'CNPJ' : 'CPF'}
                    placeholder={newEntity.type === 'COMPANY' ? '00.000.000/0000-00' : '000.000.000-00'}
                    value={newEntity.document} 
                    onChange={(e) => setNewEntity({ ...newEntity, document: e.target.value })} 
                    leftIcon={<Hash className="w-4 h-4" />} 
                  />
                  
                  {newEntity.type === 'COMPANY' && (
                    <Input 
                      label="Nome Fantasia" 
                      placeholder="Nome comercial (opcional)" 
                      value={newEntity.tradeName} 
                      onChange={(e) => setNewEntity({ ...newEntity, tradeName: e.target.value })} 
                      leftIcon={<FileText className="w-4 h-4" />} 
                    />
                  )}
                  
                  <Input 
                    label="E-mail" 
                    type="email" 
                    placeholder="contato@empresa.com" 
                    value={newEntity.email} 
                    onChange={(e) => setNewEntity({ ...newEntity, email: e.target.value })} 
                    leftIcon={<Mail className="w-4 h-4" />} 
                  />
                  
                  <Input 
                    label="Telefone" 
                    placeholder="(00) 00000-0000" 
                    value={newEntity.phone} 
                    onChange={(e) => setNewEntity({ ...newEntity, phone: e.target.value })} 
                    leftIcon={<Phone className="w-4 h-4" />} 
                  />
                </div>

                <div className="flex gap-4 pt-6 border-t border-surface-700">
                  <Button variant="ghost" className="flex-1" onClick={() => setShowSetupWizard(false)}>
                    Cancelar
                  </Button>
                  <Button className="flex-1" onClick={handleSetupEntity}>
                    Continuar
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Bank Connection */}
            {setupStep === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                    <Plug className="w-5 h-5 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">Conexão Bancária</h3>
                    <p className="text-sm text-surface-400">Configure as credenciais do provider de pagamento</p>
                  </div>
                </div>

                {/* Success Card - Entity Created */}
                {createdEntity && (
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm text-green-400 font-medium">Entidade criada com sucesso</p>
                      <p className="text-white font-semibold">{createdEntity.name}</p>
                      <p className="text-xs text-surface-400">{createdEntity.document}</p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <Input 
                      label="Nome da Conexão" 
                      placeholder="Ex: OpenPix Principal, Asaas Backup" 
                      value={newConnection.name} 
                      onChange={(e) => setNewConnection({ ...newConnection, name: e.target.value })} 
                      leftIcon={<Plug className="w-4 h-4" />} 
                    />
                    <p className="text-xs text-surface-500 mt-1.5 ml-1">
                      💡 Nome para identificar esta conexão internamente. Use algo descritivo como "OpenPix Principal" ou "Asaas Backup".
                    </p>
                  </div>
                  
                  <Select 
                    label="Provider de Pagamento" 
                    options={[
                      { value: 'OPENPIX', label: '🟢 OpenPix (Pix)' },
                      { value: 'IUGU', label: '🔵 iugu (Multi-pagamento)' },
                      { value: 'ASAAS', label: '🟣 Asaas (Conta Digital)' }
                    ]} 
                    value={newConnection.provider} 
                    onChange={(e) => setNewConnection({ ...newConnection, provider: e.target.value })} 
                  />
                  
                  <Input 
                    label="API Key / App ID" 
                    type="password" 
                    placeholder="Chave de API do provider" 
                    value={newConnection.apiKey} 
                    onChange={(e) => setNewConnection({ ...newConnection, apiKey: e.target.value })} 
                    leftIcon={<Key className="w-4 h-4" />} 
                  />
                </div>

                {/* Provider Info Card */}
                <div className="p-4 bg-surface-800/50 border border-surface-700 rounded-xl">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-primary-400 mt-0.5" />
                    <div className="text-sm">
                      <p className="text-surface-300 font-medium mb-1">Onde encontrar a API Key?</p>
                      <p className="text-surface-400">
                        {newConnection.provider === 'OPENPIX' && 'OpenPix: Admin Panel → Permissions → APIs → New API/Plugin'}
                        {newConnection.provider === 'IUGU' && 'iugu: Configurações → Integração → API Token'}
                        {newConnection.provider === 'ASAAS' && 'Asaas: Configurações → Integrações → API → Gerar Token'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-6 border-t border-surface-700">
                  <Button variant="ghost" className="flex-1" onClick={() => setSetupStep(1)}>
                    Voltar
                  </Button>
                  <Button className="flex-1" onClick={handleSetupBankConnection}>
                    Continuar
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Bolsão Account */}
            {setupStep === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">Conta Bolsão</h3>
                    <p className="text-sm text-surface-400">A conta principal que centraliza todos os recursos do tenant</p>
                  </div>
                </div>

                {/* Success Cards - Previous Steps */}
                {createdEntity && createdBankConnection && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-green-400" />
                        </div>
                        <div>
                          <p className="text-xs text-green-400 font-medium">Entidade</p>
                          <p className="text-white font-semibold text-sm truncate">{createdEntity.name}</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                          <Plug className="w-4 h-4 text-green-400" />
                        </div>
                        <div>
                          <p className="text-xs text-green-400 font-medium">Conexão</p>
                          <p className="text-white font-semibold text-sm truncate">{createdBankConnection.name}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <Input 
                      label="Nome da Conta" 
                      placeholder="Ex: Conta Principal, Bolsão Master" 
                      value={newAccount.name} 
                      onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })} 
                      leftIcon={<Wallet className="w-4 h-4" />} 
                    />
                  </div>
                  
                  <Select 
                    label="Tipo de Chave Pix" 
                    options={[
                      { value: 'RANDOM', label: '🔑 Chave Aleatória (EVP)' },
                      { value: 'CNPJ', label: '🏢 CNPJ' },
                      { value: 'CPF', label: '👤 CPF' },
                      { value: 'EMAIL', label: '📧 E-mail' },
                      { value: 'PHONE', label: '📱 Telefone' }
                    ]} 
                    value={newAccount.pixKeyType} 
                    onChange={(e) => setNewAccount({ ...newAccount, pixKeyType: e.target.value })} 
                  />
                  
                  <Input 
                    label="Chave Pix" 
                    placeholder={
                      newAccount.pixKeyType === 'RANDOM' ? 'Será gerada automaticamente pelo provider' :
                      newAccount.pixKeyType === 'CNPJ' ? '00.000.000/0000-00' :
                      newAccount.pixKeyType === 'CPF' ? '000.000.000-00' :
                      newAccount.pixKeyType === 'EMAIL' ? 'email@empresa.com' :
                      '+5511999999999'
                    }
                    value={newAccount.pixKeyType === 'RANDOM' ? '' : newAccount.pixKey} 
                    onChange={(e) => setNewAccount({ ...newAccount, pixKey: e.target.value })} 
                    leftIcon={<Key className="w-4 h-4" />}
                    disabled={newAccount.pixKeyType === 'RANDOM'}
                  />
                </div>

                {/* Summary Card */}
                <div className="p-4 bg-primary-500/10 border border-primary-500/30 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-primary-400 mt-0.5" />
                    <div className="text-sm">
                      <p className="text-primary-300 font-medium mb-1">Resumo da Configuração</p>
                      <p className="text-surface-400">
                        Ao finalizar, será criada uma conta bolsão vinculada à entidade <strong className="text-white">{createdEntity?.name}</strong> usando 
                        a conexão <strong className="text-white">{createdBankConnection?.name}</strong> ({createdBankConnection?.provider}).
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-6 border-t border-surface-700">
                  <Button variant="ghost" className="flex-1" onClick={() => setSetupStep(2)}>
                    Voltar
                  </Button>
                  <Button className="flex-1" onClick={handleSetupAccount}>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Finalizar Configuração
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </Modal>

      {/* Contract Modal */}
      {selectedVirtualAccountForContract && (
        <VirtualAccountContractModal
          isOpen={showContractModal}
          onClose={() => {
            setShowContractModal(false);
            setSelectedVirtualAccountForContract(null);
          }}
          virtualAccountId={selectedVirtualAccountForContract.id}
          virtualAccountName={selectedVirtualAccountForContract.user?.name || 'Conta Virtual'}
          onSuccess={() => fetchVirtualAccounts(selectedTenant!.id)}
        />
      )}
    </div>
  );
}
